import { canonicalTonAddress } from './tonAddress'

export interface PositionTradeDraft {
  side: 'buy' | 'sell'
  token: string
  wallet: string | null
  percent: string
  preset: number
  createdAt: number
  /** Explicit row click: use the existing executor after a fresh target-bound read. */
  executeSell?: boolean
}
export type PositionFormDraft = PositionTradeDraft & { id: string }
export const POSITION_DRAFT_TTL_MS = 120_000
export const QUICK_SELL_TTL_MS = 10_000
export const positionDraftDeadline = (
  draft: Pick<PositionTradeDraft, 'createdAt' | 'executeSell'>
) => draft.createdAt + (draft.executeSell ? QUICK_SELL_TTL_MS : POSITION_DRAFT_TTL_MS)

export function positionDraftFresh(
  draft: Pick<PositionTradeDraft, 'createdAt' | 'executeSell'>,
  now = Date.now()
): boolean {
  return (
    Number.isFinite(draft.createdAt) && now >= draft.createdAt && now < positionDraftDeadline(draft)
  )
}

export function sellPercent(raw: string): string | null {
  const text = raw.trim().replace(',', '.')
  if (!/^\d+(?:\.\d{1,2})?$/.test(text)) return null
  const n = Number(text)
  return Number.isFinite(n) && n > 0 && n <= 100 ? text : null
}

/** A merged Portfolio row has a Sell source only when it represents primary alone. */
export function positionSourceWallet(
  addresses: readonly string[],
  primary: string | null
): string | null {
  const key = canonicalTonAddress(primary)
  return key !== null && addresses.length === 1 && canonicalTonAddress(addresses[0] ?? null) === key
    ? primary
    : null
}

/** Buy/legacy drafts prefill; only an explicit executeSell row click may launch Sell. */
export function positionDraftFromState(
  state: unknown,
  token: string,
  wallet: string | null,
  now = Date.now()
): PositionTradeDraft | null {
  if (!state || typeof state !== 'object' || !('positionTrade' in state)) return null
  const draft = state.positionTrade as Partial<PositionTradeDraft> | null
  if (!draft || (draft.side !== 'buy' && draft.side !== 'sell')) return null
  if (draft.executeSell !== undefined && typeof draft.executeSell !== 'boolean') return null
  if (draft.executeSell && (draft.side !== 'sell' || !draft.percent)) return null
  if (typeof draft.token !== 'string') return null
  if ((canonicalTonAddress(draft.token) ?? draft.token) !== (canonicalTonAddress(token) ?? token))
    return null
  if (draft.wallet !== null) {
    const source = typeof draft.wallet === 'string' ? canonicalTonAddress(draft.wallet) : null
    if (source === null || source !== canonicalTonAddress(wallet)) return null
  } else if (draft.side === 'sell') return null
  if (
    typeof draft.createdAt !== 'number' ||
    !positionDraftFresh({ createdAt: draft.createdAt, executeSell: draft.executeSell }, now)
  )
    return null
  if (
    typeof draft.percent !== 'string' ||
    (draft.percent !== '' && sellPercent(draft.percent) === null)
  )
    return null
  if (!Number.isInteger(draft.preset) || draft.preset! < 0 || draft.preset! > 2) return null
  return {
    ...draft,
    token,
    percent: draft.percent === '' ? '' : sellPercent(draft.percent)!,
  } as PositionTradeDraft
}

export function withoutPositionDraft(state: unknown): unknown {
  if (!state || typeof state !== 'object') return state
  return Object.fromEntries(
    Object.entries(state).filter(([key]) => key !== 'positionTrade' && key !== 'positionTradeId')
  )
}

/** Canonical replaces keep the originating click's identity. */
export function positionNavigationId(state: unknown, fallback: string): string {
  const id = (state as { positionTradeId?: unknown } | null)?.positionTradeId
  return typeof id === 'string' && id.length > 0 && id.length <= 128 ? id : fallback
}

/** Recheck at navigation handoff: the deadline may pass after render. */
export function withoutExpiredPositionDraft(state: unknown, now = Date.now()): unknown {
  if (!state || typeof state !== 'object' || !('positionTrade' in state)) return state
  const draft = state.positionTrade as Partial<PositionTradeDraft> | null
  if (
    draft?.executeSell === true &&
    (typeof draft.createdAt !== 'number' ||
      !positionDraftFresh({ createdAt: draft.createdAt, executeSell: true }, now))
  )
    return withoutPositionDraft(state)
  return state
}
